import { useCallback, useRef } from "react";
import { pricingUtils } from "@flights/web-api-utils-universal";
import { type UIPrice } from "@flights/types";

import useTrackerContext from "../useTrackerContext";
import useGlobalContext from "../useGlobalContext";
import useRouteName from "../useRouteName";
import { type RouteName } from "@flights/constants";

const ACTION_NAME = "flights__price_rendered";
const ACTION_VERSION = "3.1.0";

export type RouteNameWithPaymentPriceChange = RouteName | "checkout-payment-price-change";

export type PriceRenderedUiComponent = "BREAKDOWN" | "TAG";

export type PriceRenderedProductPrice = {
  productType: string;
  productPrice: UIPrice;
};

type PriceRenderedPrice = {
  currency_code?: string;
  price?: string;
};

type PriceRenderedProduct = {
  price?: PriceRenderedPrice;
  product_type?: string;
};

type PriceRenderedContent = {
  funnel_page: RouteNameWithPaymentPriceChange;
  product_id?: string;
  products?: PriceRenderedProduct[];
  request_id: string;
  total_price: PriceRenderedPrice;
  ui_component: PriceRenderedUiComponent;
};

type Content = PriceRenderedContent;

type TrackingData = {
  requestId: string;
};

const buildEventSignature = (uiComponent: PriceRenderedUiComponent, productId: string, totalPrice: UIPrice) => {
  return [uiComponent, productId, totalPrice.currencyCode, totalPrice.units, totalPrice.nanos].join("-");
};

export const usePriceConsistencyMonitoringEvents = () => {
  const { trackExternal } = useTrackerContext();
  const routName = useRouteName();
  const { requestId } = useGlobalContext();
  const lastTrackedRef = useRef<string>("");

  return useCallback(
    (
      uiComponent: PriceRenderedUiComponent,
      productId: string,
      productPrices: PriceRenderedProductPrice[],
      totalPrice: UIPrice,
      isPaymentPriceChange?: boolean
    ) => {
      // Prevent duplicate tracking: create a unique signature for this event
      const eventSignature = buildEventSignature(uiComponent, productId, totalPrice);
      if (lastTrackedRef.current === eventSignature) {
        return;
      }
      lastTrackedRef.current = eventSignature;

      const trackingData: TrackingData = { requestId };
      const content = getContent(
        isPaymentPriceChange ? "checkout-payment-price-change" : routName,
        trackingData,
        uiComponent,
        productId,
        productPrices,
        totalPrice
      );

      trackExternal({
        type: "c360",
        args: [
          {
            action_name: ACTION_NAME,
            action_version: ACTION_VERSION,
            content
          }
        ]
      });
    },
    [requestId, routName, trackExternal]
  );
};

const getContent = (
  routeName: RouteNameWithPaymentPriceChange,
  trackingData: TrackingData,
  uiComponent: PriceRenderedUiComponent,
  productId: string,
  productPrices: PriceRenderedProductPrice[],
  totalPrice: UIPrice
): Content => {
  // Map productPrices to products array, removing duplicate product_id
  const products: PriceRenderedProduct[] = productPrices.map(({ productPrice, productType }) => ({
    price: {
      currency_code: productPrice.currencyCode,
      price: String(pricingUtils.priceToValue(productPrice))
    },
    product_type: productType
  }));

  const total = {
    currency_code: totalPrice.currencyCode,
    price: String(pricingUtils.priceToValue(totalPrice))
  };

  return {
    funnel_page: routeName,
    product_id: productId, // Moved to top level (v3.0.0 schema)
    products,
    request_id: trackingData.requestId,
    total_price: total,
    ui_component: uiComponent
  };
};

export default usePriceConsistencyMonitoringEvents;
